import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Plus, Loader2, Shield, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useWorkspace } from '@/store/workspace';
import { useAuthStore } from '@/store/auth';
import { supabase } from '@/lib/supabase';

const roleConfig = {
  admin: { label: 'Admin', className: 'bg-purple-100 text-purple-800' },
  reader: { label: 'Reader', className: 'bg-blue-100 text-blue-800' },
  superadmin: { label: 'Superadmin', className: 'bg-amber-100 text-amber-800' },
} as const;

function RolePill({ role }: Readonly<{ role: 'admin' | 'reader' | 'superadmin' | null }>) {
  const config = role ? roleConfig[role] : { label: 'Unknown', className: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function WorkspaceStatus({ isMember, isSuperadminAccess }: Readonly<{ isMember: boolean; isSuperadminAccess: boolean }>) {
  let dotColor = "bg-gray-300";
  let label = "View only";

  if (isMember) {
    dotColor = "bg-green-400";
    label = "Active";
  } else if (isSuperadminAccess) {
    dotColor = "bg-amber-400";
    label = "Assume role";
  }

  return (
    <div className="flex items-center space-x-1 text-gray-600">
      <div className={`w-2 h-2 rounded-full ${dotColor}`}></div>
      <span>{label}</span>
    </div>
  );
}

interface WorkspaceForRoleSelection {
  id: string;
  name: string;
}

export function WorkspaceSelection() {
  const navigate = useNavigate();
  const { workspaces, isLoading, fetchWorkspaces, selectWorkspace } = useWorkspace();
  const { isSuperadmin } = useAuthStore();
  const [selecting, setSelecting] = useState<string | null>(null);
  const [roleSelectionWorkspace, setRoleSelectionWorkspace] = useState<WorkspaceForRoleSelection | null>(null);
  const [workspaceOwners, setWorkspaceOwners] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchWorkspaces(isSuperadmin);
  }, [isSuperadmin]);

  // Fetch workspace owners for superadmins
  useEffect(() => {
    const fetchOwners = async () => {
      if (!isSuperadmin) return;

      try {
        const { data, error } = await supabase.functions.invoke('get-workspace-owners');
        if (error) {
          console.error('Failed to fetch workspace owners:', error);
          return;
        }
        setWorkspaceOwners(data.owners || {});
      } catch (err) {
        console.error('Failed to fetch workspace owners:', err);
      }
    };

    fetchOwners();
  }, [isSuperadmin]);

  const handleSelectWorkspace = async (workspaceId: string) => {
    try {
      setSelecting(workspaceId);
      await selectWorkspace(workspaceId);
      navigate('/home');
    } catch (error) {
      console.error('Failed to select workspace:', error);
      setSelecting(null);
    }
  };

  const handleWorkspaceClick = (workspace: typeof workspaces[0]) => {
    if (workspace.role === 'superadmin') {
      // Superadmin accessing non-member workspace - ask for role
      setRoleSelectionWorkspace({ id: workspace.id, name: workspace.name });
    } else if (workspace.is_member) {
      // Regular member - open directly
      handleSelectWorkspace(workspace.id);
    }
  };

  const handleRoleSelected = async (role: 'admin' | 'reader') => {
    if (!roleSelectionWorkspace) return;

    try {
      setSelecting(roleSelectionWorkspace.id);
      setRoleSelectionWorkspace(null);
      await selectWorkspace(roleSelectionWorkspace.id, role);
      navigate('/home');
    } catch (error) {
      console.error('Failed to select workspace with assumed role:', error);
      setSelecting(null);
    }
  };

  const handleCreateWorkspace = () => {
    navigate('/workspace/create');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-ingesta-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading workspaces...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-ingesta-blue-600 rounded-full mb-4">
            <Building className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Select Your Workspace
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose a workspace to continue, or create a new one to get started with your project management.
          </p>
        </div>

        {/* Workspaces Grid */}
        {workspaces.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {workspaces.map((workspace) => {
                const isMember = workspace.is_member;
                const isClickable = isMember || workspace.role === 'superadmin';
                return (
                  <Card
                    key={workspace.id}
                    className={
                      isClickable
                        ? "cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
                        : "opacity-60 cursor-not-allowed border-dashed"
                    }
                    onClick={() => isClickable && handleWorkspaceClick(workspace)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <CardTitle className={`text-sm font-medium truncate ${!isClickable && "text-gray-500"}`}>
                            {workspace.name}
                          </CardTitle>
                          {workspace.role === 'superadmin' && workspaceOwners[workspace.id] && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              Owner: {workspaceOwners[workspace.id]}
                            </p>
                          )}
                        </div>
                        {isClickable && selecting === workspace.id && (
                          <Loader2 className="h-4 w-4 animate-spin text-ingesta-blue-600 shrink-0" />
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm">
                        {/* Role pill */}
                        <RolePill role={workspace.role} />
                        <WorkspaceStatus isMember={isMember} isSuperadminAccess={workspace.role === 'superadmin'} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Divider */}
            <div className="flex items-center my-8">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-4 text-sm text-gray-500 bg-gray-50">or</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
          </>
        ) : (
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No workspaces found
            </h3>
            <p className="text-gray-600 mb-6">
              You don't have access to any workspaces yet. Create your first workspace to get started.
            </p>
          </div>
        )}

        {/* Create New Workspace */}
        <Card className="border-2 border-dashed border-gray-300 hover:border-ingesta-blue-400 transition-colors">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 bg-ingesta-blue-100 rounded-full flex items-center justify-center mb-4">
              <Plus className="h-6 w-6 text-ingesta-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Create New Workspace
            </h3>
            <p className="text-gray-600 text-center mb-6 max-w-sm">
              Set up a new workspace for your team and start managing projects together.
            </p>
            <Button
              onClick={handleCreateWorkspace}
              size="lg"
              className="gap-2"
            >
              <Plus className="h-5 w-5" />
              Create Workspace
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Role Selection Dialog for Superadmin Access */}
      <Dialog open={!!roleSelectionWorkspace} onOpenChange={(open) => !open && setRoleSelectionWorkspace(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Role</DialogTitle>
            <DialogDescription>
              Choose which role you'd like to assume when accessing{' '}
              <span className="font-semibold">{roleSelectionWorkspace?.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-4 whitespace-normal text-left"
              onClick={() => handleRoleSelected('admin')}
            >
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">Admin</div>
                <div className="text-sm text-gray-500">Full access to manage workspace settings, members, and all projects</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-4 whitespace-normal text-left"
              onClick={() => handleRoleSelected('reader')}
            >
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">Reader</div>
                <div className="text-sm text-gray-500">View-only access to projects and dashboards</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}